

import { computed, defineComponent, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import Decimal from 'decimal.js'
import { formatDate, formatThousands } from '@/utils'
import {
  generateReimbApprovalForms, getReimbRequestByIds, setReimbPaymentDate
} from '@/apis/application-form/reimbursementRequest'
import { GroupModel } from '@/components/collapse/CollapseGroupInterface'
import {
  ReimbRequestIdListModel, ReimbRequestModel, ReimbRequestSetPaymentDateModel
} from '@/model/application-form/reimbursementRequest'
import Container from '@/components/form/container/Container.vue'
import BaseDialog from '@/components/dialog/BaseDialog.vue'
import DynamicCollapse from '@/components/collapse/DynamicCollapse.vue'
import { ElMessage } from 'element-plus'

export default defineComponent({
  name: 'GenerateApprovalReimbDialog',
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    groupedReimbList: {
      type: Array as () => number[],
      required: true,
      default: []
    },
    confirmText: {
      type: String,
      default: 'message.confirm'
    },
    cancelText: {
      type: String,
      default: 'message.cancel'
    }
  },
  components: { BaseDialog, Container, DynamicCollapse },
  emits: ['update:modelValue', 'update:groupedReimbList', 'refresh'],
  setup(props, context) {
    const { t } = useI18n()
    // 创建本地副本
    const localIdList = ref<number[]>(
      props.groupedReimbList && props.groupedReimbList.length > 0
        ? props.groupedReimbList
        : []
    )
    const localGroups = ref<GroupModel[]>([])
    // 使用 computed 实现双向绑定
    const dialogVisible = computed({
      get: () => props.modelValue,
      set: (value) => context.emit('update:modelValue', value)
    })
    const dynamicCollapseRef = ref()
    const selectedDate = ref()
    const datePickerDialogVisible = ref(false)
    const orderTableStruct = [
      { prop: 'id', label: t('common.indexNumber'), width: '80px' },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'amount', label: t('bank.amount'), formatter: (row: any) => formatThousands(row.amount), align: 'right' },
      {
        prop: 'paymentDate',
        label: t('order.paymentDate'),
        formatter: (row: ReimbRequestModel) => {
          return formatDate(row.paymentDate, 'yyyy-MM-dd')
        }
      },
      { prop: 'comment', label: t('common.notes') },
      { prop: 'purpose', label: t('order.purposePayeeMayView'), width: '200px' }
    ]
    // 创建一个处理多个模型并分组的函数
    const createGroupsFromModels = (models: ReimbRequestModel[]): GroupModel[] => {
      if (!models || models.length === 0) {
        return []
      }

      const map = new Map<string, GroupModel>()

      for (const item of models) {
        const key = `${item.employeeNameCn}_${item.employeeNameEn}_${item.employeeId}_${formatDate(item.paymentDate, 'yyyy-MM-dd')}`

        if (!map.has(key)) {
          map.set(key, {
            key: key,
            name: `${item.employeeNameCn}, ${item.employeeNameEn}, (${item.employeeId})`,
            accountNumber: item.employeeId,
            currency: 'CNY',
            paymentDate: item.paymentDate ? formatDate(item.paymentDate, 'yyyy-MM-dd') : '',
            totalAmount: '',
            tableList: []
          })
        }

        const group = map.get(key)
        if (group) {
          group.tableList.push(item)
          if (group.totalAmount) {
            group.totalAmount = new Decimal(group.totalAmount).plus(new Decimal(item.amount)).toString()
          } else {
            group.totalAmount = new Decimal(item.amount).toString()
          }
        }
      }

      return Array.from(map.values())
    }

    const confirmDialog = async() => {
      if (localIdList.value === null || localIdList.value === undefined || localIdList.value.length === 0) {
        dialogVisible.value = false
        return
      }
      for (const valueElement of localGroups.value) {
        let totalAmount
        try {
          totalAmount = Number(valueElement.totalAmount)
        } catch (e) {
          ElMessage({
            message: '金额格式错误',
            type: 'error',
            duration: 2000
          })
          return
        }
        if (totalAmount <= 0) {
          ElMessage({
            message: valueElement.name + ' 合计金额需大于0',
            type: 'error',
            duration: 2000
          })
          return
        }
      }
      const params: ReimbRequestIdListModel = {
        idList: localIdList.value
      }
      const res = await generateReimbApprovalForms(params)
      if (res?.data && res?.data > 0) {
        ElMessage({
          message: t('common.createSuccess'),
          type: 'success',
          duration: 2000
        })
        context.emit('refresh')
      } else {
        ElMessage({
          message: res?.msg,
          type: 'error',
          duration: 2000
        })
      }
      dialogVisible.value = false
    }
    const closeDialog = () => {
      dialogVisible.value = false
    }
    const handlePaymentDate = () => {
      if (dynamicCollapseRef.value && dynamicCollapseRef.value.getSelectionRows().totalCount > 0) {
        selectedDate.value = null
        datePickerDialogVisible.value = true
      } else {
        ElMessage({
          message: t('order.selectTip'),
          type: 'error'
        })
      }
    }

    const handleRemoveItem = () => {
      if (dynamicCollapseRef.value && dynamicCollapseRef.value.getSelectionRows().totalCount > 0) {
        const result = dynamicCollapseRef.value.getSelectionRows()
        const { byGroup } = result
        const newIdList = [] as number[]
        const newGroups = localGroups.value
          .map((group, groupIndex) => {
            if (!byGroup[groupIndex]) {
              // 未被删除的组也要重新算 totalAmount
              const total = group.tableList.reduce((sum, r) => {
                return new Decimal(sum).plus(new Decimal(r.amount || 0))
              }, new Decimal(0)).toString()
              return { ...group, totalAmount: total }
            }

            // 被删除的组
            const removeIds = new Set(byGroup[groupIndex].map((r: any) => r.id))

            // 新 tableList
            const newTableList = group.tableList.filter(row => !removeIds.has(row.id))
            // 重新计算 totalAmount
            const newTotalAmount = newTableList.reduce((sum, r) => {
              return new Decimal(sum).plus(new Decimal(r.amount || 0))
            }, new Decimal(0)).toString()

            return {
              ...group,
              tableList: newTableList,
              totalAmount: newTotalAmount
            }
          }).filter(group => {
            if (group.tableList.length > 0) {
              group.tableList.forEach(table => {
                newIdList.push(table.id)
              })
              return true
            }
          })
        // 更新到 v-model
        localIdList.value = newIdList
        localGroups.value = newGroups
      } else {
        ElMessage({
          message: t('order.selectTip'),
          type: 'error'
        })
      }
    }
    const getReimbByIds = async(idList: any) => {
      const params: ReimbRequestIdListModel = {
        idList: idList
      }
      const res = await getReimbRequestByIds(params)
      if (res?.data && res?.data.length > 0) {
        localGroups.value = createGroupsFromModels(res.data)
      } else {
        ElMessage({
          message: res?.msg,
          type: 'error',
          duration: 2000
        })
      }
    }
    const onDatePickerDialogConfirm = async() => {
      if (selectedDate.value != null) {
        const params: ReimbRequestSetPaymentDateModel = {
          idList: dynamicCollapseRef.value.getSelectionRows().allRows.map((row: any) => row.id),
          paymentDate: formatDate(selectedDate.value)
        }
        const res = await setReimbPaymentDate(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.setSuccess'),
            type: 'success',
            duration: 2000
          })
          datePickerDialogVisible.value = false
          await getReimbByIds(localIdList.value)
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
      } else {
        ElMessage({
          message: t('order.setPaymentDateP'),
          type: 'error'
        })
      }
    }
    onMounted(() => {
      getReimbByIds(props.groupedReimbList)
    })
    return {
      t,
      confirmDialog,
      closeDialog,
      handlePaymentDate,
      handleRemoveItem,
      onDatePickerDialogConfirm,
      dialogVisible,
      datePickerDialogVisible,
      dynamicCollapseRef,
      orderTableStruct,
      localGroups,
      selectedDate
    }
  }
})
